import { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {Button, CircularProgress, Grid, TextField} from '@mui/material';
import { Stack } from '@mui/system';
import { useWeb3Auth } from 'app/providers/Web3AuthProvider';
import { userActions, userSelector } from 'entities/user';
import {BORDER_RADIUS_XXL, COLOR_WHITE, uiSelector} from 'shared/config';
import { useShallowSelector } from 'shared/hooks';
import { EngagementType, EngagementTypePath, RequestStatus, UpdateUserRequest } from 'shared/types';
import { checkRequestStatusEquality, pinata} from 'shared/utils';
import { useTranslation } from 'react-i18next';


import { connectionDefaultValues, ConnectionFormState, connectionValidationSchema } from '../model';
import { AgreementCheckbox, GenderSelectBlock, WeddingTypeBlock } from '../ui';
import {Typography} from "../../../shared/ui/components";

interface IConnectionContainerProps {
  partnerPromoCode?: string;
}

export const ConnectionContainer: FC<IConnectionContainerProps> = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const uiState = useShallowSelector(uiSelector.getUI);
  const [weddingType, setWeddingType] = useState<EngagementType | null>(EngagementType.Person);

  const { isLoading: isWeb3AuthLoading } = useWeb3Auth();
  const { weddingDate, firstName, lastName, sex, email, profileImage } = useShallowSelector(userSelector.getUser);
  // @ts-ignore
  const {handleSubmit, control, reset, getValues, formState: { errors, isValid, isSubmitting },} = useForm<ConnectionFormState>({
    // @ts-ignore
    resolver: yupResolver(connectionValidationSchema),
    defaultValues: connectionDefaultValues,
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const isUpdateUserInfoSuccess = checkRequestStatusEquality(
    uiState[userActions.updateUserInfo.type],
    RequestStatus.SUCCESS,
  );

  const onSave: SubmitHandler<UpdateUserRequest> = (data) => {
    dispatch(
      userActions.updateUserInfo({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        weddingDate: data.weddingDate,
        profileImage: data.profileImage,
        sex: data.sex,
      }),
    );
  };

  useEffect(() => {
    reset({ weddingDate, profileImage, email, sex, firstName, lastName, agree: false });
  }, [ weddingDate, profileImage, email, sex, firstName, lastName, reset]);

  const handleNavigate = () => {
    if (weddingType) {
      navigate(EngagementTypePath[weddingType]);
    }
  };


  const handleFileChange = async (e:any) => {
      if (e.target.files && e.target.files[0]) {

          try {
              const upload = await pinata.upload.file(e.target.files[0]);
              const ipfsUrl = await pinata.gateways.convert(upload.IpfsHash);
              let data = getValues();
              data.profileImage = ipfsUrl;
              dispatch(userActions.uploadImageProfile({
                  firstName: data.firstName,
                  lastName: data.lastName,
                  weddingDate: data.weddingDate,
                  profileImage: data.profileImage
              }));
          } catch (error) {
              console.log(error);
          }
      }

  };

  useEffect(() => {
    if (isUpdateUserInfoSuccess && weddingType) {
      navigate(EngagementTypePath[weddingType]);
      dispatch({ type: `${userActions.updateUserInfo?.type}_RESET` });
    }
    // Reason: navigate only on updateUserInfoSuccess, not on weddingType change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isUpdateUserInfoSuccess, navigate]);

  return (
    <>
        <Stack sx={{alignItems: 'center'}}>
            <Typography variant="h3" sx={{
                fontSize: '38px',
                fontWeight: '500',
                lineHeight: '0.95',
                textAlign: 'center',
                mt: { xs: 3, md: 0 },
                fontFamily: 'Montserrat, sans-serif'
            }} className="center">{t('cc_title')}</Typography>
            <Typography variant="h2"  sx={{
                marginTop: '12px',
                fontFamily: 'Tektur, serif',
                fontSize: {xs: '16px', md: '18px'},
                lineHeight: {xs: '1.25', md: '1.25'},
                fontWeight: '400',
                background: 'radial-gradient(circle, #2df6de 20%, #3c5dfe 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textSlign: 'center',
                maxWidth: '500px',
                marginBottom: '20px !important'
            }}  className="center">{t('cc_description')}</Typography>
        </Stack>
      <Stack
        component="form"
        onSubmit={handleSubmit(onSave)}
        sx={{ alignItems: 'center', width: '100%', maxWidth: '550px', mx: 'auto', mt: { xs: 2, md: 2 }, pl: { xs: 2, md: 'auto'} , pr: { xs: 2, md: 'auto'} }}
      >
        <Stack gap={3} width="100%">
          <Stack spacing={4} width="100%" alignItems="center">
              <Stack
                  mt={3}
                  alignItems="center"
                  sx={{
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                  }}
              >
                  <Controller
                      name="profileImage"
                      control={control}
                      render={({ field }) => (
                          <TextField
                              {...field}
                              placeholder={"Your foto for certificate, 400*400"}
                              sx={{ marginRight: '20px', maxWidth: '420px', display: "none" }}
                              disabled={true}
                              hidden={true}
                          />
                      )}
                  />
                  <Stack sx={{
                      padding: '4px',
                      border: '2px solid #2df6de',
                      borderRadius: '100%',
                      top: '0px',
                      position: 'absolute',
                      height: '120px',
                      width: '120px'
                  }}>
                      <img src={profileImage} alt='photo' style={{borderRadius: '100%', width: '110px', height: '110px', objectFit: 'cover'}} />
                  </Stack>
                  <Button
                      variant="contained"
                      component="label"
                      htmlFor="contained-button-file"
                      sx={{ padding: '10px 30px', maxWidth: '120px', marginTop: '95px' }}
                  >
                      {t('Upload')}
                      <Controller
                          name="picture"
                          control={control}
                          render={({ field }) => (
                              <TextField
                                  {...field}
                                  sx={{ display: "none" }}
                                  id="contained-button-file"
                                  error={!!errors.profileImage}
                                  type="file"
                                  onChange={handleFileChange}
                                  hidden={true}
                              />
                          )}
                      />
                  </Button>
              </Stack>
            <Stack>
                <Typography sx={{
                    textAlign: 'center',
                    fontSize: '16px',
                    color: '#ccc',
                }}>
                    {email}
                </Typography>
                <Stack sx={{display: 'none'}}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t('Email')}
                                placeholder={t('Email')}
                                disabled={!!email}
                                error={!!errors.email}
                                helperText={errors.email ? errors.email.message : ''}
                            />

                        )}
                    />
                </Stack>
            </Stack>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Name')}
                  placeholder={t('Name')}
                  disabled={!!firstName}
                  error={!!errors.firstName}
                  helperText={errors.firstName ? errors.firstName.message : ''}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('FamilyName')}
                  placeholder={t('FamilyName')}
                  disabled={!!lastName}
                  error={!!errors.lastName}
                  helperText={errors.lastName ? errors.lastName.message : ''}
                />
              )}
            />

            <Stack sx={{ width: '180px'}}>
                <Controller
                    name="weddingDate"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={t('WeddingDate')}
                            placeholder={t('WeddingDate')}
                            disabled={!!weddingDate}
                            error={!!errors.weddingDate}
                            type="date"
                            InputLabelProps={{
                                shrink: true
                            }}
                            helperText={errors.weddingDate ? errors.weddingDate.message : ''}
                        />)}
                />
            </Stack>
          </Stack>
          // @ts-ignore
          <GenderSelectBlock control={control} disabled={!!sex} />
          {((!firstName && !lastName) || !sex) && !isWeb3AuthLoading && <AgreementCheckbox control={control} />}
          <WeddingTypeBlock selectedWeddingType={weddingType} setSelectedWeddingType={(wt) => setWeddingType(wt)} />
        </Stack>

        {(!firstName && !lastName) || !sex ? (
          <Button
            type="submit"
            variant="outlined"
            disabled={isSubmitting || !isValid || isWeb3AuthLoading || !weddingType}
            sx={{ mt: 5, width: 'auto' }}
          >
            {isSubmitting || isWeb3AuthLoading ? (
              <CircularProgress size={30} sx={{ color: COLOR_WHITE }} />
            ) : (
                t('NextStep')
            )}
          </Button>
        ) : (
          <Button variant="outlined" disabled={!weddingType} onClick={handleNavigate} sx={{ 
              mt: 5, 
              width: 'auto'
          }}>
            {t('NextStep')}
          </Button>
        )}
      </Stack>

      {/* {firstName && lastName && promoCode && sex && ( */}
    </>
  );
};
